"use client";

import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Camera,
  CheckCircle,
  FileText,
  MapPin,
  Phone,
  Shield,
  UploadCloud,
  Landmark,
  LandmarkIcon,
  Building,
  Building2,
} from "lucide-react";
import { useState, useEffect, useMemo } from "react";
import {
  useGetUserProfileInfo,
  useUpdateUserProfilePhoto,
  useGetCityDataMutation,
  useUpateUserProfile,
  useUpdateUserDoc,
  useGetUserKycList,
} from "@/hooks/useCommon";
import { toast } from "react-toastify";
import { FullPageSpinner } from "@/components/common/FullPageSpinner";
import { useAuth, User } from "@/store/auth";
import { useCity } from "@/store/common";
import { debounce } from "lodash";
import { useSearchParams, useRouter } from "next/navigation";
import UserPersonalDetailForm from "./UserPersonalDetailForm";
import UserChangePasswordForm from "./UserChangePasswordForm";
import UserCompanyDetailForm from "./UserCompanyDetailForm";
import UserBankDetailForm from "./UserBankDetailForm";
import UserVehicleDetailForm from "./UserVehicleDetailForm";
import UserPaymentUploadForm from "./UserPaymentUploadForm";
import UserDutyDetailsForm from "./UserDutyDetailsForm";
import UserLicenseDetailForm from "./UserLicenseDetailForm";
import UserOtherDetails from "./UserOtherDetails";
import UserSMSTable from "./UserSMSTable";
import UserEmailTable from "./UserEmailTable";
import UserStatusHistory from "./UserStatusHistory";
import UserAllocationHistory from "./UserAllocationHistory";
import UserBookingHistory from "./UserBookingHistory";
import DriverLedger from "./DriverLedger";
import UserPaymentHistory from "./UserPaymentHistory";
import UserStatus from "./UserStatus";
import ProfileKYCForm from "./ProfileKYCForm";
import UserFriendsFamilyDetailForm from "./ProfileFriendsFamilyForm";
import UserKYCDetailForm from "./UserKYCDetailForm";
import UserReportsForm from "./UserReports";
import UserCoworkerForm from "./UserCoworkerForm";
import MasterCompanyDetailComponnent from "./MasterCompanyDetail";

export const schema = z.object({
  first_name: z.string().min(1, "First Name is required"),
  last_name: z.string().min(1, "Last Name is required"),
  father_name: z.string().optional(),
  alternate_mobile: z.string().optional(),
  external_ref: z.string().optional(),
  gender: z.enum(["Male", "Female", "Other"], {
    required_error: "Gender is required",
  }),
  address: z.string().optional(),
  // state_id: z.string().min(1, "State is required"),
  // city: z.string().min(1, "City is required"),
  pincode: z.string().min(1, "Pin Code is required"),
  kyc_type: z.string().min(1, "KYC Type is required"),
  kyc: z.string().min(1, "KYC is required"),
  newsletter_subscription: z.boolean().optional(),
  landline_number: z.string().optional(),
  new_password: z.string().min(1, "New Password is required"),
  confirm_password: z.string().min(1, "Confirm Password is required"),

  last_updated_on: z.string().optional(),
  verified_by: z.string().optional(),
});

export type FormData = z.infer<typeof schema>;

const profileNavBtns = [
  { key: "personal-detail", label: "Personal Detail" },
  { key: "company-detail", label: "Company Detail" },
  // { key: "vehicle-detail", label: "Vehicle Detail" },
  { key: "duty-payment-details", label: "Preferences" },
  { key: "kyc-verify", label: "KYC Verify" },
  { key: "change-password", label: "Change Password" },
  { key: "bank-detail", label: "Bank Detail" },
  { key: "payment-upload", label: "Payment Upload" },
  { key: "license-details", label: "License Details" },
  { key: "co-worker", label: "Add Coworkers" },
  { key: "other-details", label: "Other Details" },
  // { key: "smses", label: "SMSes" },
  // { key: "emails", label: "Emails" },
  // { key: "status-history", label: "Status History" },
  // { key: "allocation-history", label: "Allocation History" },
  // { key: "booking-history", label: "Booking History" },
  // { key: "driver-ledger", label: "Driver Ledger" },
  // { key: "payment-history", label: "Payment History" },
  // { key: "status", label: "Status" },
  // { key: "reports", label: "Reports" },
  { key: "completed", label: "Completed" },
];

export default function ProfilePage() {
  const [profilePreview, setProfilePreview] = useState<string | null>(null);
  const [profileNavBtn, setProfileNavBtn] = useState<string>("personal-detail");
  const searchParams = useSearchParams();
  const user_id = searchParams.get("user_id");
  const path = searchParams.get("type");
  const { user: authUser } = useAuth();
  const userId = path && user_id ? Number(user_id) : (authUser?.id ?? null);
  const validUserId = userId && userId > 0 ? userId : null;
  const {
    data: userProfile,
    isLoading: isProfileLoading,
    isError,
    error,
  } = useGetUserProfileInfo(validUserId ?? -1);

  const { mutate: getCityData } = useGetCityDataMutation();
  const { cityData } = useCity();

  const navItems = [
    {
      key: "personal-detail",
      label: "Personal Detail",
      component: <UserPersonalDetailForm />,
    },
    {
      key: "company-detail",
      label: "Company Detail",
      component: <MasterCompanyDetailComponnent />,
    },
    // {
    //   key: "vehicle-detail",
    //   label: "Vehicle Detail",
    //   component: <UserVehicleDetailForm />,
    // },
    {
      key: "duty-payment-details",
      label: "Preferences",
      component: <UserDutyDetailsForm />,
    },
        {
      key: "kyc-verify",
      label: "KYC Verify",
      component: <UserKYCDetailForm />,
    },
    {
      key: "change-password",
      label: "Change Password",
      component: <UserChangePasswordForm />,
    },
    {
      key: "bank-detail",
      label: "Bank Detail",
      component: <UserBankDetailForm />,
    },
    {
      key: "payment-upload",
      label: "Payment Upload",
      component: <UserPaymentUploadForm />,
    },
    {
      key: "license-details",
      label: "License Details",
      component: <UserLicenseDetailForm />,
    },
      ...(path === "driver"
      ? [
          {
            key: "other-details",
            label: "Other Details",
            component: <UserOtherDetails />,
          },
        ]
      : ["This module is only for the Driver"]),
    {
      key: "co-worker",
      label: "Add Coworkers",
      component: <UserCoworkerForm />,
    },
    // { key: "smses", label: "SMSes", component: <UserSMSTable /> },
    // { key: "emails", label: "Emails", component: <UserEmailTable /> },
    // {
    //   key: "status-history",
    //   label: "Status History",
    //   component: <UserStatusHistory />,
    // },
    // {
    //   key: "allocation-history",
    //   label: "Allocation History",
    //   component: <UserAllocationHistory />,
    // },
    // {
    //   key: "booking-history",
    //   label: "Booking History",
    //   component: <UserBookingHistory />,
    // },
    // {
    //   key: "driver-ledger",
    //   label: "Driver Ledger",
    //   component: <DriverLedger />,
    // },
    // {
    //   key: "payment-history",
    //   label: "Payment History",
    //   component: <UserPaymentHistory />,
    // },
    // { key: "status", label: "Status", component: <UserStatus /> },
    // { key: "reports", label: "Reports", component: <UserReportsForm /> },
  ];

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      newsletter_subscription: false,
    },
  });

  const [debouncedGetCity, setDebouncedGetCity] = useState(() => () => {});
  const { mutate: uploadPhoto } = useUpdateUserProfilePhoto(validUserId ?? -1);
  const { mutate: uploadDoc } = useUpdateUserDoc(validUserId ?? -1);
  const { data: kycData, isLoading } = useGetUserKycList("kyc_proof");

  const kycList = useMemo(() => {
    return kycData || [];
  }, [kycData]);

  const profilePath = process.env.NEXT_PUBLIC_API_PIC_URL;
  const updateMutation = useUpateUserProfile();
  useEffect(() => {
    if (userProfile) {
      reset({
        first_name: userProfile.first_name || "",
        last_name: userProfile.last_name || "",
        father_name: userProfile.father_name || "",
        alternate_mobile: userProfile.alternate_mobile || "",
        external_ref: userProfile.external_ref || "",
        gender: userProfile.gender || "",
        address: userProfile.address || "",
        // state_id: userProfile.state_id || "",
        // city: userProfile.city || "",
        pincode: userProfile.pincode?.toString() || "",
        kyc_type: userProfile.kyc_type?.toString() || "",
        kyc: userProfile.kyc || "",
        newsletter_subscription: userProfile.newsletter_subscription ?? false,
        landline_number: userProfile.landline_number || "",
      });

      // if (userProfile.city) {
      //   getCityData(userProfile.city, {
      //     onSuccess: (res: any) => {
      //       if (res?.length) {
      //         const city = res[0];
      //
      //         const label = `${city.name} (${city.state_name}, ${city.country_code})`;
      //         setCityInput(label);
      //       }
      //     },
      //   });
      // }
    }
  }, [userProfile, reset]);

  useEffect(() => {
    const fn = debounce((val: string) => {
      if (!val) return;
      getCityData(val);
    }, 500);

    setDebouncedGetCity(() => fn);

    return () => fn.cancel();
  }, [getCityData]);

  // if (isProfileLoading) return <FullPageSpinner />;

  const onSubmit = async (data: FormData) => {
    try {
      const formattedData = {
        ...data,
        father_name: data.father_name?.trim() || "",
        alternate_mobile: data.alternate_mobile?.trim() || "",
        external_ref: data.external_ref?.trim() || "",
        address: data.address?.trim() || "",
        newsletter_subscription: data.newsletter_subscription || false,
        landline_number: data.landline_number?.trim() || "",
        userId: user_id,
      };

      updateMutation.mutate(formattedData, {
        onSuccess: () => {
          toast.success("Profile updated successfully!", {
            position: "top-right",
            autoClose: 5000,
          });
        },
        onError: (err: any) => {
          toast.error(err.response?.data?.message || "register failed", {
            position: "top-right",
            autoClose: 5000,
          });
        },
      });
    } catch (error) {
      toast.error("Failed to register. Try again.");
    }
  };

  // const debouncedGetCity = useMemo(() => {
  //   return debounce((val: string) => {
  //     if (!val) {
  //       return;
  //     }
  //     getCityData(val);
  //   }, 500);
  // }, [getCityData]);

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfilePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
      uploadPhoto({ file, folder: "profile" });
    }
  };
  const selectedKycType = watch("kyc_type");

  // const rawFileName = userProfile?.user_profile_path?.split("/").pop();
  // const profilePicture = `${profilePath}${encodeURIComponent(
  //   userProfile?.user_profile_path ?? ""
  // )}`;
  const fileParts = userProfile?.user_profile_path?.split("/");
  const folderPath = fileParts?.slice(0, -1).join("/");
  const fileName =
    fileParts !== undefined ? fileParts[fileParts.length - 1] : "";

  const profilePicture = `${profilePath}${folderPath}/${encodeURIComponent(
    fileName
  )}`;

  const [profilePercentage, setProfilePercentage] = useState<number>(0);
  const [completedSections, setCompletedSections] = useState<string[]>([
    "personal-detail",
  ]);

  const handleComplete = (sectionKey: string) => {
    setCompletedSections((prev) =>
      prev.includes(sectionKey) ? prev : [...prev, sectionKey]
    );
  };

  useEffect(() => {
    setProfilePercentage(Number((100 / profileNavBtns.length - 1).toFixed(1)));
  }, []);

  useEffect(() => {
    const percentage = (completedSections.length / profileNavBtns.length) * 100;
    setProfilePercentage(parseFloat(percentage.toFixed(1)));
  }, [completedSections]);

  return (
    <div className="min-h-screen bg-gray-100 py-8 px-4">
      <div className="flex gap-2 flex-col lg:flex-row">
        {/* <div className="left-bar lg:pl-4">
          <ul className="grid grid-cols-2 lg:flex lg:items-start lg:flex-col">
            {profileNavBtns.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setProfileNavBtn(key)}
                className={`border h-10 lg:w-[200px] text-start px-3 border-gray-300 cursor-pointer text-[10px] font-[500] ${
                  profileNavBtn === key ? "bg-[#101828] text-white" : "bg-white"
                }`}
              >
                {label}
              </button>
            ))}
          </ul>
        </div>  */}

        {/* <div className="left-bar p-4 bg-white w-[25%] h-[max-content]">
          <h2 className="text-[12px] font-semibold text-gray-800 mb-3">
            FILL PROFILE IN FEW STEPS
          </h2>
          <div className="mb-6">
            <div className="flex justify-between text-xs text-gray-600 font-medium mb-1">
              <span className="text-[10px]">Overall Progress</span>
              <span className="text-[10px]">{profilePercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${profilePercentage}%` }}
              />
            </div>
          </div>

   
          <ul className="space-y-3 relative">
            {profileNavBtns.map(({ key, label },index) => {
              const isCompleted = completedSections.includes(key);
              const isActive = profileNavBtn === key;

              return (
                <div className="relative">
                  <li key={key} className="group">
                    <button
                      onClick={() => setProfileNavBtn(key)}
                      className={`flex items-center w-full px-3 py-2 text-left text-sm font-medium border rounded-md transition-colors duration-150 
              ${
                isActive
                  ? "bg-[#101828] text-white border-[#101828]"
                  : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
              }
            `}
                    >
                      <div className="flex items-center gap-2 text-[10px]">
                        <span
                          className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold border 
                  ${
                    isCompleted
                      ? "bg-green-400 text-white border-green-400"
                      : isActive
                      ? "text-white bg-white border-white"
                      : "bg-white text-gray-400 border-gray-400"
                  }`}
                        >
                          {isCompleted ? "✓" : ""}
                        </span>
                        {label}
                      </div>
                    </button>
                  </li>
                  {
                    index !== profileNavBtns.length - 1 && <div className="absolute transform w-[13px] h-[4px] bg-gray-300 left-[13px] bottom-[-8px] rotate-[90deg] border border-gray-300 "></div>
                  }
                </div>
              );
            })}
          </ul>
        </div>  */}

        <div className="left-bar p-4 bg-white w-full lg:w-[25%] h-max mb-4 lg:mb-0">
          <h2 className="text-[12px] font-semibold text-gray-800 mb-3">
            FILL PROFILE IN FEW STEPS
          </h2>

          <div className="mb-6">
            <div className="flex justify-between text-xs text-gray-600 font-medium mb-1">
              <span className="text-[10px]">Overall Progress</span>
              <span className="text-[10px]">{profilePercentage}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                style={{ width: `${profilePercentage}%` }}
              />
            </div>
          </div>

          <ul className="space-y-3 relative">
            {profileNavBtns.map(({ key, label }, index) => {
              const isCompleted = completedSections.includes(key);
              const isActive = profileNavBtn === key;

              return (
                <div className="relative" key={key}>
                  <li className="group">
                    <button
                      onClick={() => setProfileNavBtn(key)}
                      className={`flex items-center w-full px-3 py-2 text-left text-sm font-medium border rounded-md transition-colors duration-150 
                ${
                  isActive
                    ? "bg-[#101828] text-white border-[#101828]"
                    : "bg-white text-gray-800 border-gray-300 hover:bg-gray-50"
                }`}
                    >
                      <div className="flex items-center gap-2 text-[10px]">
                        <span
                          className={`w-4 h-4 rounded-full flex items-center justify-center text-[10px] font-bold border 
                    ${
                      isCompleted
                        ? "bg-green-400 text-white border-green-400"
                        : isActive
                        ? "text-white bg-white border-white"
                        : "bg-white text-gray-400 border-gray-400"
                    }`}
                        >
                          {isCompleted ? "✓" : ""}
                        </span>
                        {label}
                      </div>
                    </button>
                  </li>
                  {index !== profileNavBtns.length - 1 && (
                    <div className="absolute transform w-[13px] h-[4px] bg-gray-300 left-[13px] bottom-[-8px] rotate-[90deg] border border-gray-300 " />
                  )}
                </div>
              );
            })}
          </ul>
        </div>

        {/* <div className="flex-1">
          {navItems.find((item) => item.key === profileNavBtn)?.component}
        </div> */}

        <div className="flex-1">
          {profileNavBtn === "other-details" && path !== "driver" ? (
            <div className="text-center text-gray-600 mt-8">
              <h2>This module is only for the Driver</h2>
            </div>
          ) : (
            // Render the content based on the selected profileNavBtn
            (navItems.find((item) => typeof item === 'object' && 'key' in item && item.key === profileNavBtn) as { component: React.ReactNode } | undefined)?.component
          )}
        </div>
      </div>
    </div>
  );
}
